import {createClient, Client, ClientOptions, SubscribePayload} from 'graphql-ws';
import {IGraphQLSubscriptionClient, SubscriptionNotation} from './IGraphQLSubscriptionClient';
import {getSingleAttributeValue} from "./getSingleAttributeValue";
import {getJwtExpMs} from "../../utils/user";

type CallbackFn = (data: any) => void;

interface GraphQLWSClientConfig {
    url: string;
    connectionParams?: ClientOptions['connectionParams'];
    retryAttempts?: number;
    // Optional proactive reconnect skew (ms before exp). Defaults to 60s
    preExpirySkewMs?: number;
}

export class GraphQLWSClient implements IGraphQLSubscriptionClient {
    private client: Client;
    private url: string;
    private retryAttempts: number;
    private connectionParams?: ClientOptions['connectionParams'];
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private preExpirySkewMs: number;
    // exp (ms) of the token we last proactively reconnected for; guards against
    // reconnect loops when the auth cache keeps returning the same near-expiry token
    private lastProactiveReconnectExpMs = 0;

    private isConnected = false;
    private isDisposed = false;
    private isReconnecting = false;
    private lastConnectedAt = 0;

    private subscriptions: Map<string, () => void> = new Map();
    private subscriptionDefs: Map<string, SubscriptionNotation> = new Map();

    constructor(config: GraphQLWSClientConfig) {
        this.connectionParams = config.connectionParams;
        this.url = config.url;
        this.retryAttempts = config.retryAttempts ?? 5;
        this.preExpirySkewMs = config.preExpirySkewMs ?? 60_000;

        this.client = this.createWsClient(this.connectionParams);

        if (typeof window !== 'undefined') {
            window.addEventListener('online', this.checkConnectionLiveness);
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    private handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            this.checkConnectionLiveness();
        }
    }

    private createWsClient(connectionParams?: ClientOptions['connectionParams']): Client {
        return createClient({
            url: this.url,
            connectionParams,
            lazy: false,
            retryAttempts: this.retryAttempts,
            connectionAckWaitTimeout: 5000,
            retryWait: async (retries) => {
                const jitter = Math.floor(Math.random() * 300);
                const delay = Math.min(30000, 1000 * Math.pow(2, retries)) + jitter;
                // console.log(`[WS] retryWait ${retries} → waiting ${delay}ms`);
                return new Promise(resolve => setTimeout(resolve, delay));
            },
            shouldRetry: (errOrCloseEvent) => {
                if (this.isDisposed) {
                    return false;
                }
                if (errOrCloseEvent instanceof CloseEvent) {
                    console.warn('[WS] socket closed with code', errOrCloseEvent.code);
                } else {
                    console.warn('[WS] connection error, will retry', errOrCloseEvent);
                }
                return true;
            },
            on: {
                connected: async () => {
                    this.isConnected = true;
                    this.lastConnectedAt = Date.now();
                    // Proactively schedule reconnect based on token expiry
                    try {
                        const token = await this.extractTokenFromConnectionParams();
                        if (token) {
                            const expMs = getJwtExpMs(token);
                            this.scheduleProactiveReconnect(expMs);
                        }
                    } catch (e) {
                        // non-fatal
                        console.warn('[WS] failed to schedule proactive reconnect', e);
                    }
                    this.resubscribeAll();
                },
                closed: (event: any) => {
                    console.warn('[WS] Closed:', event.reason || event);
                    this.isConnected = false;
                    this.subscriptions.clear();
                    this.clearProactiveReconnect();
                    // If server closed due to expired token, reconnect immediately
                    if (event && typeof event.code === 'number' && event.code === 4401) {
                        // expedite reconnect rather than waiting for backoff
                        this.retryConnection().catch(() => {/* ignore */});
                    }
                },
            },
        });
    }

    public retryConnection = async (): Promise<void> => {
        if (this.isDisposed || this.isReconnecting) return;
        this.isReconnecting = true;
        try {
            await this.disposeClient();
            this.client = this.createWsClient(this.connectionParams);
        } finally {
            this.isReconnecting = false;
        }
    };

    private checkConnectionLiveness = () => {
        if (this.isDisposed || this.isReconnecting) return;
        if (!this.isConnected) {
            this.retryConnection().catch(() => {/* ignore */});
            return;
        }
        // If the connection has been "connected" for over 5 minutes without any
        // reconnect, it may be stale (TCP died silently while tab was in background).
        // Force a reconnect to get a fresh connection with fresh subscriptions.
        const stalenessThreshold = 5 * 60_000;
        const timeSinceConnect = Date.now() - this.lastConnectedAt;
        if (timeSinceConnect > stalenessThreshold) {
            console.warn('[WS] Connection may be stale, forcing reconnect');
            this.retryConnection().catch(() => {/* ignore */});
        }
    };

    registerSubscription(id: string, query: string, cb: CallbackFn, queryParams: Record<string, any> = {}): void {
        // console.log('registerSubscription', this.subscriptions.has(id));
        if (this.subscriptions.has(id)) {
            this.subscriptionDefs.set(id, {id, query, cb, params: queryParams});
            // console.log(`[WS] Subscription ${id} already exists. Updated callback.`);
            return;
        }

        const payload: SubscribePayload = {
            query,
            variables: {...queryParams},
        };

        const dispose = this.client.subscribe(payload, {
            next: ({data}) => {
                // console.log('ws data', data);
                if (data) {
                    cb(getSingleAttributeValue(data));
                } else {
                    console.warn(`[WS] Subscription ${id} received empty data payload`);
                }
            },
            error: (err) => {
                console.error(`[WS] Error in subscription ${id}`, err);
            },
            complete: () => {
                this.subscriptions.delete(id);
                // console.log(`[WS] Subscription ${id} completed`)
            },
        });
        // console.log("this.subscriptions.set");

        this.subscriptions.set(id, dispose);
        this.subscriptionDefs.set(id, {id, query, cb, params: queryParams});
    }


    cancelSubscription(id: string): void {
        this.subscriptionDefs.delete(id);
        const dispose = this.subscriptions.get(id);
        if (dispose) {
            dispose();
            this.subscriptions.delete(id);
            this.subscriptionDefs.delete(id);
            // console.log(`[WS] Subscription ${id} cancelled`);
        }
    }


    dispose(): void {
        this.isDisposed = true;
        for (const dispose of this.subscriptions.values()) {
            dispose();
        }
        this.subscriptionDefs.clear();
        this.disposeClient();

        if (typeof window !== 'undefined') {
            window.removeEventListener('online', this.checkConnectionLiveness);
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    private async disposeClient(): Promise<void> {
        if (this.client) {
            try {
                this.subscriptions.clear();
                await this.client.dispose();
            } catch (err) {
                console.error('[WS] Error during client disposal', err);
            }
        }
        this.isConnected = false;
        this.clearProactiveReconnect();
    }

    private resubscribeAll(): void {
        const defs = new Map(this.subscriptionDefs);
        for (const [id, {query, cb, params}] of defs.entries()) {
            this.registerSubscription(id, query, cb, params);
        }
    }

    private async extractTokenFromConnectionParams(): Promise<string | null> {
        try {
            const params = typeof this.connectionParams === 'function'
                ? await this.connectionParams()
                : this.connectionParams ?? {};
            const headerVal = (params as any)?.Authorization
                ?? (params as any)?.headers?.Authorization
                ?? (params as any)?.headers?.authorization;
            if (!headerVal || typeof headerVal !== 'string') return null;
            const m = headerVal.match(/^Bearer\s+(.+)$/i);
            return m ? m[1] : headerVal;
        } catch {
            return null;
        }
    }

    private scheduleProactiveReconnect(expMs: number) {
        this.clearProactiveReconnect();
        if (!Number.isFinite(expMs) || expMs <= 0) return;
        const now = Date.now();
        let delay = Math.max(0, expMs - now - this.preExpirySkewMs);
        if (delay === 0) {
            if (this.lastProactiveReconnectExpMs === expMs) {
                // We already proactively reconnected for this token and got the same
                // one back (auth cache won't refresh until actual expiry). Stop looping:
                // wait until the token truly expires, then reconnect to pick up a fresh one.
                console.warn('[WS] proactive reconnect returned unchanged token, deferring to actual expiry');
                delay = Math.max(1000, expMs - now + 1000);
            } else {
                // expire soon; reconnect now
                this.lastProactiveReconnectExpMs = expMs;
                this.retryConnection().catch(() => {/* ignore */});
                return;
            }
        }
        this.reconnectTimer = setTimeout(() => {
            this.lastProactiveReconnectExpMs = expMs;
            this.retryConnection().catch(() => {/* ignore */});
        }, delay);
    }

    private clearProactiveReconnect() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
}
